import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ScenarioAccessViewNode } from "../api/models/scenarioAccessView";
import { getScenarioAccessGraphql } from "../api/services/scenarioGraphqlService";
import { useApp } from "../contexts/AppContext";
import { useUsers } from "../contexts/UsersContext";
import { hasPermission as userHasPermission } from "../lib/permissions";
import type { Permission } from "../types/permissions";
import type { ComprehensiveScenario } from "../types/scenarios";
import type { AppUser } from "../types/user";

/** Effective (highest) permission a user holds on a scenario. */
export type EffectivePermission = "owner" | "edit" | "comment" | "view";

/**
 * A single row of the scenario access list — one user who can access the
 * scenario, with their effective permission and the source(s) of that access.
 * A user may be both an explicit share and inheriting access simultaneously.
 */
export interface ScenarioAccessEntry {
	userId: string;
	userName: string;
	isOwner: boolean;
	canView: boolean;
	canComment: boolean;
	canEdit: boolean;
	/** Has an explicit ScenarioShares row — controls remain removable. */
	isExplicitShare: boolean;
	/** Inherits status-conditional access — non-removable. */
	isInherited: boolean;
	permission: EffectivePermission;
}

export interface ScenarioAccessResult {
	access: ScenarioAccessEntry[];
	isLoading: boolean;
	error: string | null;
	isLive: boolean;
}

function effectivePermission(entry: {
	isOwner: boolean;
	canEdit: boolean;
	canComment: boolean;
}): EffectivePermission {
	if (entry.isOwner) return "owner";
	if (entry.canEdit) return "edit";
	if (entry.canComment) return "comment";
	return "view";
}

function mapNode(node: ScenarioAccessViewNode): ScenarioAccessEntry {
	return {
		userId: node.userId,
		userName: node.userName ?? node.userId,
		isOwner: node.isOwner,
		canView: node.canView,
		canComment: node.canComment,
		canEdit: node.canEdit,
		isExplicitShare: node.isExplicitShare,
		isInherited: node.isInherited,
		permission: effectivePermission(node),
	};
}

/**
 * Merge raw access rows into one entry per user, OR-ing the flags and keeping
 * the highest permission — mirrors the MAX() aggregation in vw_ScenarioAccess
 * so the demo fallback behaves identically to live.
 */
function dedupeByUser(rows: ScenarioAccessEntry[]): ScenarioAccessEntry[] {
	const byUser = new Map<string, ScenarioAccessEntry>();
	for (const row of rows) {
		const existing = byUser.get(row.userId);
		if (!existing) {
			byUser.set(row.userId, { ...row });
			continue;
		}
		const merged: ScenarioAccessEntry = {
			userId: row.userId,
			userName: existing.userName || row.userName,
			isOwner: existing.isOwner || row.isOwner,
			canView: existing.canView || row.canView,
			canComment: existing.canComment || row.canComment,
			canEdit: existing.canEdit || row.canEdit,
			isExplicitShare: existing.isExplicitShare || row.isExplicitShare,
			isInherited: existing.isInherited || row.isInherited,
			permission: "view",
		};
		merged.permission = effectivePermission(merged);
		byUser.set(row.userId, merged);
	}
	return Array.from(byUser.values());
}

/**
 * Build the demo-mode access list from in-memory data, mirroring the live
 * vw_ScenarioAccess arms:
 *   - owner (createdBy) with full access
 *   - explicit shares from scenario.shares
 *   - inherited holders: users with view_all_active_scenarios (active/approved)
 *     or view_parked_scenarios (parked/archived). Workflow stage assignees are
 *     intentionally excluded.
 */
function buildDemoAccess(
	scenario: ComprehensiveScenario,
	users: AppUser[],
	getUserById: (id: string) => AppUser | undefined,
): ScenarioAccessEntry[] {
	const rows: ScenarioAccessEntry[] = [];

	rows.push({
		userId: scenario.createdBy,
		userName: getUserById(scenario.createdBy)?.name ?? scenario.createdByName,
		isOwner: true,
		canView: true,
		canComment: true,
		canEdit: true,
		isExplicitShare: false,
		isInherited: false,
		permission: "owner",
	});

	for (const share of scenario.shares) {
		const canComment =
			share.permission === "comment" || share.permission === "edit";
		const canEdit = share.permission === "edit";
		rows.push({
			userId: share.userId,
			userName: getUserById(share.userId)?.name ?? share.userName,
			isOwner: false,
			canView: true,
			canComment,
			canEdit,
			isExplicitShare: true,
			isInherited: false,
			permission: canEdit ? "edit" : canComment ? "comment" : "view",
		});
	}

	// The frontend demo permission model has no dedicated
	// `view_all_active_scenarios` permission (that distinction only exists in the
	// live backend). Active scenarios are broadcast to anyone with
	// `view_scenarios` (mirrors useVisibleScenarios' active-broadcast rule),
	// while parked/archived inherit via `view_parked_scenarios`.
	const status = scenario.status;
	const inheritedPermission: Permission | null =
		status === "active"
			? "view_scenarios"
			: status === "parked" || status === "archived"
				? "view_parked_scenarios"
				: null;

	if (inheritedPermission) {
		for (const user of users) {
			if (userHasPermission(user, inheritedPermission)) {
				rows.push({
					userId: user.id,
					userName: user.name,
					isOwner: false,
					canView: true,
					canComment: false,
					canEdit: false,
					isExplicitShare: false,
					isInherited: true,
					permission: "view",
				});
			}
		}
	}

	return dedupeByUser(rows);
}

/**
 * Access list for a scenario's Sharing tab. In live mode it queries
 * vw_ScenarioAccess via GraphQL (server-gated by scenario access). In demo mode
 * it derives the same shape from in-memory scenario data.
 */
export function useScenarioAccess(
	scenario: ComprehensiveScenario | null | undefined,
): ScenarioAccessResult {
	const { mode } = useApp();
	const { users, getUserById } = useUsers();
	const isLive = mode === "live";
	const scenarioId = scenario ? Number(scenario.id) : Number.NaN;
	const enabled = isLive && Number.isFinite(scenarioId);

	const query = useQuery({
		queryKey: ["scenarioAccess", scenarioId],
		queryFn: () => getScenarioAccessGraphql(scenarioId),
		enabled,
		staleTime: 30_000,
	});

	const access = useMemo(() => {
		if (isLive) {
			if (!query.data) return [];
			return dedupeByUser(query.data.edges.map((edge) => mapNode(edge.node)));
		}
		if (!scenario) return [];
		return buildDemoAccess(scenario, users, getUserById);
	}, [isLive, query.data, scenario, users, getUserById]);

	return {
		access,
		isLoading: enabled && query.isPending,
		error:
			query.error instanceof Error
				? query.error.message
				: query.error
					? "Failed to load scenario access"
					: null,
		isLive,
	};
}
